import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Plus, 
  File, 
  Folder, 
  MoreVertical, 
  ChevronRight, 
  ChevronDown,
  Loader,
  Upload,
  FolderPlus,
  Files,
  Trash2,
  MessageCircle,
  CheckSquare,
  Square,
  Search,
  FileSpreadsheet,
  FileText,
  FileCode,
  AlertCircle,
  MessageSquare,
  Check,
  Zap
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
  } from "./ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
  } from "./ui/alert-dialog";
import { cn } from "../lib/utils";
import { FixedSizeList as List } from 'react-window';
import { useForceUpdate } from '../hooks/useForceUpdate';
import { useWebSocket } from '../contexts/WebSocketContext';
import axios from 'axios';
import { useDocuments } from '../context/DocumentContext';

// Add this near the top of the file, after imports
const fontClass = "font-inter"; // We'll use this class for consistent font application

const formatDate = (dateString) => {
  try {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }).format(date);
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid date';
  }
};

const formatFileSize = (bytes) => {
  if (!bytes || isNaN(bytes)) return '0 B';
  
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
};

const getFileIcon = (fileType) => {
  switch (true) {
    case fileType.includes('spreadsheetml.sheet'):
    case fileType.includes('ms-excel'):
    case fileType.includes('csv'):
      return <FileSpreadsheet className="w-5 h-5 text-green-500" />;
    case fileType.includes('pdf'):
      return <File className="w-5 h-5 text-red-500" />;
    case fileType.includes('text'):
      return <FileText className="w-5 h-5 text-blue-500" />;
    default:
      return <FileCode className="w-5 h-5 text-gray-500" />;
  }
};

// Helper function to get friendly file type names
const getFileTypeName = (fileType) => {
  switch (true) {
    case fileType.includes('pdf'):
      return 'PDF';
    case fileType.includes('spreadsheetml.sheet'):
    case fileType.includes('ms-excel'):
    case fileType.includes('csv'):
      return 'Spreadsheet';
    case fileType.includes('text'):
      return 'Text';
    default:
      return 'Document';
  }
};

// Main DocumentSidebar component
const DocumentSidebar = ({
  folders = [],
  selectedDocument,
  onDocumentSelect,
  onDeleteDocument,
  onNewDocument,
  selectedDocumentSet = null,  // New prop for selected document set
  documentSets = [],           // New prop for document sets
  onDocumentSetSelect,
  onCreateFolder,
  user,
  onDeleteFolder,
  onMoveDocument,
  onStartMultiChat,  // New prop for handling multi-document chat
  onCreateDocumentSet,         // New handler for creating sets
  onDeleteDocumentSet,         // New handler for deleting sets
  onRenameDocumentSet,
  // onRenameDocumentSet,         // Add this prop
  isLoading,
  error,
  uploadingFiles,
  uploadProgress,
  activeHighlight,  // Add this prop
}) => {
  const { documents, fetchDocuments, processingDocuments } = useDocuments();
  const { refreshTrigger } = useWebSocket();
  console.log('Current processingDocuments:', processingDocuments);
  // State declarations
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);
  const [expandedFolders, setExpandedFolders] = useState({});
  const [isCreateFolderDialogOpen, setIsCreateFolderDialogOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const [folderToDelete, setFolderToDelete] = useState(null);
  const [isDeleteFolderDialogOpen, setIsDeleteFolderDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [documentSetToRename, setDocumentSetToRename] = useState(null);
  const [newSetName, setNewSetName] = useState('');
  const [isRenamingSet, setIsRenamingSet] = useState(false);
  const WS_URL = process.env.REACT_APP_WS_URL || 'wss://predica.ai/ws';
  const forceUpdate = useForceUpdate();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);

  const connectWebSocket = () => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      console.error('No access token available');
      return;
    }
    
    // Encode the token for URL safety
    const encodedToken = encodeURIComponent(encodeURIComponent(token));
    const encodedUserId = encodeURIComponent(encodeURIComponent(user.id));
    
    const wsUrl = `${process.env.REACT_APP_WS_URL}/document-progress/${encodedUserId}?token=${encodedToken}`;
    
    const ws = new WebSocket(wsUrl, {
      headers: {
        'X-WS-Token': localStorage.getItem('access_token')
      }
    });
    
    ws.onopen = () => {
      ws.send(JSON.stringify({
        type: 'auth',
        token: localStorage.getItem('access_token')
      }));
    };

    ws.onerror = (error) => {
      console.error('WebSocket Error:', error);
    };
  };

  useEffect(() => {
    // Sync processing state with actual document statuses
    const activeProcessing = documents.reduce((acc, doc) => {
      if (doc.embedding_status === 'processing') {
        acc[doc.id] = {
          progress: doc.processing_progress || 0,
          status: 'processing'
        };
      }
      return acc;
    }, {});

    // setProcessingDocuments(activeProcessing);
  }, [documents]);

  // Debug logging
  useEffect(() => {
    console.log('Documents in sidebar:', documents);
    console.log('Folders in sidebar:', folders);
  }, [documents, folders]);

  useEffect(() => {
    console.log('Refresh trigger changed, re-fetch documents');
    if (fetchDocuments) {
      console.log('Fetching updated documents list');
      fetchDocuments();
    }
  }, [refreshTrigger, fetchDocuments]);

  // Remove the old useEffect that synced processing state

  // Handler functions
  const handleMultiSelect = useCallback((document) => {
    if (!document?.id) {
      console.error('Cannot select document without ID:', document);
      return;
    }

    setSelectedDocuments(prev => {
      // Create a new array with the full document objects
      const isSelected = prev.some(d => d.id === document.id);
      
      if (isSelected) {
        // Remove document from selection
        return prev.filter(d => d.id !== document.id);
      } else {
        // Add document to selection, ensuring we store the full document
        const documentToAdd = {
          ...document,
          id: document.id,
          file_type: document.file_type,
          filename: document.filename
        };
        return [...prev, documentToAdd];
      }
    });
  }, []);

  const toggleFolder = useCallback((folderId) => {
    setExpandedFolders(prev => ({
      ...prev,
      [folderId]: !prev[folderId]
    }));
  }, []);

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    
    setIsCreatingFolder(true);
    try {
      await onCreateFolder(newFolderName);
      setNewFolderName('');
      setIsCreateFolderDialogOpen(false);
    } finally {
      setIsCreatingFolder(false);
    }
  };

  const handleDeleteFolder = useCallback(async (folder) => {
    try {
      await onDeleteFolder(folder.id);
    } catch (err) {
      console.error('Error deleting folder:', err);
    }
  }, [onDeleteFolder]);

  const handleDocumentSelect = (document, isMultiSelect = false) => {
    if (!document?.id) {
      console.error('Cannot select document without ID');
      return;
    }

    const freshDocument = documents.find(d => d.id === document.id);
    if (!freshDocument) {
      console.error('Document not found in current list');
      return;
    }

    if (isMultiSelectMode) {
      // First check if document is selectable type
      const isPDF = freshDocument.file_type.includes('pdf');
      const isSpreadsheet = [
        'spreadsheet', 
        'excel', 
        'csv',
        'vnd.openxmlformats-officedocument.spreadsheetml',
        'vnd.ms-excel'
      ].some(type => freshDocument.file_type.includes(type));

      if (!isPDF && !isSpreadsheet) {
        alert('Only PDFs and Spreadsheets can be selected together');
        return;
      }

      // Then validate type consistency
      if (selectedDocuments.length > 0) {
        const firstDoc = selectedDocuments[0];
        const firstIsPDF = firstDoc.file_type.includes('pdf');
        const firstIsSpreadsheet = [
          'spreadsheet', 
          'excel', 
          'csv',
          'vnd.openxmlformats-officedocument.spreadsheetml',
          'vnd.ms-excel'
        ].some(type => firstDoc.file_type.includes(type));

        // Check category match (PDF vs spreadsheet)
        if ((firstIsPDF && !isPDF) || (firstIsSpreadsheet && !isSpreadsheet)) {
          alert('Can only select documents of the same type (PDFs or Spreadsheets)');
          return;
        }

        // Additional check for exact type matching within category
        const typeCategory = firstIsPDF ? 'pdf' : 'spreadsheet';
        if (typeCategory === 'spreadsheet' && 
            getFileTypeName(freshDocument.file_type) !== getFileTypeName(firstDoc.file_type)) {
          alert('All spreadsheets in selection must be the same format');
          return;
        }
      }

      setSelectedDocuments(prev => {
        const exists = prev.some(d => d.id === freshDocument.id);
        return exists 
          ? prev.filter(d => d.id !== freshDocument.id)
          : [...prev, freshDocument];
      });
    } else {
      onDocumentSelect(freshDocument);
    }
  };

  const onDragEnd = (result) => {
    const { destination, source, draggableId } = result;

    if (!destination) return;

    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    onMoveDocument(draggableId, destination.droppableId === 'root' ? null : destination.droppableId);
  };

  const handleMultiSelectToggle = () => {
    setIsMultiSelectMode(!isMultiSelectMode);
    if (isMultiSelectMode) {
        setSelectedDocuments([]);
    }
  };

  const handleCreateDocumentSet = () => {
      if (selectedDocuments.length < 2) return;
      onCreateDocumentSet(selectedDocuments);
      setIsMultiSelectMode(false);
      setSelectedDocuments([]);
  };

  const handleDeleteSelected = useCallback(async () => {
    if (!selectedDocuments.length) return;

    console.log('Selected documents before deletion:', selectedDocuments);

    try {
      // Delete documents one by one
      for (const doc of selectedDocuments) {
        if (!doc?.id) {
          console.error('Invalid document ID:', doc);
          continue;
        }
        console.log('Deleting document with ID:', doc.id);
        await onDeleteDocument(doc.id);
      }

      // Clear selection after successful deletion
      setSelectedDocuments([]);
      setIsMultiSelectMode(false);
    } catch (error) {
      console.error('Error deleting documents:', error);
      alert('Failed to delete some documents. Please try again.');
    }
  }, [selectedDocuments, onDeleteDocument]);

  // Add this helper function at the top level of the component
  const isCompatibleDocumentType = (doc1, doc2) => {
    const isPDF1 = doc1.file_type.includes('pdf');
    const isPDF2 = doc2.file_type.includes('pdf');
    
    const isSpreadsheet1 = [
      'spreadsheet', 
      'excel', 
      'csv',
      'vnd.openxmlformats-officedocument.spreadsheetml',
      'vnd.ms-excel'
    ].some(type => doc1.file_type.includes(type));
    
    const isSpreadsheet2 = [
      'spreadsheet', 
      'excel', 
      'csv',
      'vnd.openxmlformats-officedocument.spreadsheetml',
      'vnd.ms-excel'
    ].some(type => doc2.file_type.includes(type));

    // If both are PDFs or both are spreadsheets
    if ((isPDF1 && isPDF2) || (isSpreadsheet1 && isSpreadsheet2)) {
      // For spreadsheets, ensure they're the exact same type
      if (isSpreadsheet1 && isSpreadsheet2) {
        return getFileTypeName(doc1.file_type) === getFileTypeName(doc2.file_type);
      }
      return true;
    }
    return false;
  };

  // Define sub-components
  const DocumentItemWrapper = React.memo(({ 
    document, 
    isSelected,
    isMultiSelectMode,
    user,
    onDocumentSelect,
    onDeleteDocument,
    onMoveDocument,
    folders,
    selectedDocuments,
    handleMultiSelect,
    setIsCreateFolderDialogOpen,
    processingDocuments
  }) => {
    // Check if document is being processed (includes both upload and processing)
    const isProcessing = processingDocuments[document.id] || 
      ['pending', 'processing'].includes(document.embedding_status);
    const progress = processingDocuments[document.id]?.progress || 
      document.processing_progress || 0;
    const status = processingDocuments[document.id]?.status || 
      document.embedding_status;

    // Determine if document is interactive
    const isInteractive = !isProcessing;

    const handlePress = useCallback((e) => {
      e.stopPropagation();
      if (isInteractive) {
        if (isMultiSelectMode) {
          // First check if document is selectable type
          const isPDF = document.file_type.includes('pdf');
          const isSpreadsheet = [
            'spreadsheet', 
            'excel', 
            'csv',
            'vnd.openxmlformats-officedocument.spreadsheetml',
            'vnd.ms-excel'
          ].some(type => document.file_type.includes(type));

          if (!isPDF && !isSpreadsheet) {
            alert('Only PDFs and Spreadsheets can be selected together');
            return;
          }

          // Then validate type consistency if there are already selected documents
          if (selectedDocuments.length > 0) {
            const firstDoc = selectedDocuments[0];
            const firstIsPDF = firstDoc.file_type.includes('pdf');
            const firstIsSpreadsheet = [
              'spreadsheet', 
              'excel', 
              'csv',
              'vnd.openxmlformats-officedocument.spreadsheetml',
              'vnd.ms-excel'
            ].some(type => firstDoc.file_type.includes(type));

            // Check category match (PDF vs spreadsheet)
            if ((firstIsPDF && !isPDF) || (firstIsSpreadsheet && !isSpreadsheet)) {
              alert('Can only select documents of the same type (PDFs or Spreadsheets)');
              return;
            }

            // Additional check for exact type matching within category
            if (isSpreadsheet && 
                getFileTypeName(document.file_type) !== getFileTypeName(firstDoc.file_type)) {
              alert('All spreadsheets in selection must be the same format');
              return;
            }
          }
          
          handleMultiSelect(document);
        } else {
          onDocumentSelect(document);
        }
      }
    }, [isInteractive, isMultiSelectMode, handleMultiSelect, onDocumentSelect, document, selectedDocuments]);

    return (
      <div
        onClick={handlePress}
        onTouchStart={handlePress}
        className={cn(
          "relative flex items-center p-2 rounded-lg group",
          "hover:bg-gray-100/50 dark:hover:bg-gray-800/50",
          selectedDocument?.id === document.id ? 'bg-gray-100/80 dark:bg-gray-800/80' : '',
          isProcessing ? 'opacity-75' : '',
          !isInteractive && 'cursor-not-allowed'
        )}
      >
        <div 
          role="button" 
          tabIndex={0}
          className="flex items-center min-w-0 flex-1"
        >
          <div className="flex-shrink-0 mr-2">
            {isProcessing ? (
              <Loader className="w-5 h-5 text-blue-500 animate-spin" />
            ) : (
              getFileIcon(document.file_type)
            )}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
              {document.filename}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {formatDate(document.created_at)}
              {isProcessing && (
                <span className={cn(
                  "ml-2",
                  {
                    'text-blue-500': status !== 'failed',
                    'text-red-500': status === 'failed'
                  }
                )}>
                  {status === 'failed' ? 'Processing failed' : 'Processing...'}
                </span>
              )}
            </p>
          </div>
        </div>

        {/* Progress bar */}
        {isProcessing && (
          <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gray-200 dark:bg-gray-700">
            <div 
              className={cn(
                "h-full transition-all duration-300",
                {
                  'bg-blue-500': status !== 'failed',
                  'bg-red-500': status === 'failed'
                }
              )}
              style={{ width: `${progress}%` }}
            />
          </div>
        )}

        {/* Only show actions menu if document is interactive */}
        {isInteractive && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <button className="opacity-0 group-hover:opacity-100 p-1.5 hover:bg-gray-200 
                dark:hover:bg-gray-700 rounded-lg transition-colors ml-2">
                <MoreVertical className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem>
                <DropdownMenu>
                  <DropdownMenuTrigger className="flex items-center w-full">
                    <Folder className="w-4 h-4 mr-2" />
                    Move to Folder
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-48">
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        onMoveDocument(document.id, 'root');
                      }}
                    >
                      <Folder className="w-4 h-4 mr-2" />
                      Root
                    </DropdownMenuItem>
                    {folders.map(folder => (
                      <DropdownMenuItem
                        key={folder.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          onMoveDocument(document.id, folder.id);
                        }}
                      >
                        <Folder className="w-4 h-4 mr-2" />
                        {folder.name}
                      </DropdownMenuItem>
                    ))}
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsCreateFolderDialogOpen(true);
                      }}
                      className="border-t border-gray-200 dark:border-gray-700"
                    >
                      <FolderPlus className="w-4 h-4 mr-2" />
                      New Folder
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </DropdownMenuItem>
              <DropdownMenuItem 
                className="text-red-600 dark:text-red-400"
                onClick={(e) => {
                  e.stopPropagation();
                  onDeleteDocument(document.id);
                }}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {isMultiSelectMode && (
          <div className="mr-3 order-first">
            <div className={cn(
              "w-5 h-5 flex items-center justify-center rounded border-2",
              isSelected ? "bg-blue-500 border-blue-500" : "bg-white dark:bg-gray-800 border-gray-300"
            )}>
              {isSelected && <Check className="w-4 h-4 text-white" />}
            </div>
          </div>
        )}
      </div>
    );
  }, (prevProps, nextProps) => {
    return (
      prevProps.document.id === nextProps.document.id &&
      prevProps.isSelected === nextProps.isSelected &&
      prevProps.isMultiSelectMode === nextProps.isMultiSelectMode &&
      prevProps.processingDocuments === nextProps.processingDocuments &&
      prevProps.selectedDocuments === nextProps.selectedDocuments
    );
  });

  const DocumentSetItem = React.memo(({ set }) => (
    <div
      className={cn(
        "flex items-center p-2 rounded-lg cursor-pointer group",
        "hover:bg-gray-100 dark:hover:bg-gray-700",
        selectedDocumentSet?.id === set.id && 'bg-blue-50 dark:bg-blue-900/20'
      )}
      onClick={() => onDocumentSetSelect(set)}
    >
      <Files className="w-5 h-5 mr-2 text-blue-500" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
          Document Set ({set.documents.length})
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {set.documents.map(doc => doc.filename).join(', ').slice(0, 30)}...
        </p>
      </div>
      <DropdownMenu>
        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
          <button className="invisible group-hover:visible p-1">
            <MoreVertical className="w-4 h-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem 
            className="text-red-600"
            onClick={() => onDeleteDocumentSet(set.id)}
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete Set
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  ));

  const FolderItem = ({ folder, documents }) => {
    const folderDocuments = documents.filter(doc => doc.folder_id === folder.id);
    
    return (
      <div className="group">
        <div className="flex items-center justify-between w-full">
          <button
            onClick={() => toggleFolder(folder.id)}
            className="flex-1 flex items-center justify-between p-2 hover:bg-gray-100/50 dark:hover:bg-gray-800/50 rounded-lg"
          >
            <div className="flex items-center space-x-2">
              {expandedFolders[folder.id] ? (
                <ChevronDown className="w-4 h-4 text-gray-600 dark:text-gray-300" />
              ) : (
                <ChevronRight className="w-4 h-4 text-gray-600 dark:text-gray-300" />
              )}
              <Folder className="w-5 h-5 text-yellow-500 dark:text-yellow-400" />
              <span className="text-sm text-gray-800 dark:text-white">
                {folder.name}
              </span>
            </div>
            <span className="text-xs text-gray-500 dark:text-gray-300">
              {folderDocuments.length} items
            </span>
          </button>
          
          {/* Add dropdown menu back */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button 
                className="p-1 opacity-0 group-hover:opacity-100 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md ml-2"
                onClick={(e) => e.stopPropagation()}
              >
                <MoreVertical className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem 
                className="text-red-600"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteFolder(folder);
                }}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Folder
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {expandedFolders[folder.id] && (
          <div className="ml-6 pl-2 border-l-2 border-gray-200/30 dark:border-gray-600/30">
            {folderDocuments.map(renderDocument)}
          </div>
        )}
      </div>
    );
  };

  const renderDocument = useCallback((document) => {
    // Ensure we're checking the ID properly
    const isSelected = selectedDocuments.some(d => d.id === document.id);
    
    return (
      <DocumentItemWrapper
        key={document.id}
        document={document}
        isSelected={isSelected}
        activeHighlight={activeHighlight}
        onDocumentSelect={onDocumentSelect}
        onDeleteDocument={onDeleteDocument}
        onMoveDocument={onMoveDocument}
        user={user}
        folders={folders}
        isMultiSelectMode={isMultiSelectMode}
        selectedDocuments={selectedDocuments}
        handleMultiSelect={handleMultiSelect}
        setIsCreateFolderDialogOpen={setIsCreateFolderDialogOpen}
        processingDocuments={processingDocuments}
      />
    );
  }, [
    selectedDocuments,
    activeHighlight,
    onDocumentSelect,
    onDeleteDocument,
    onMoveDocument,
    folders,
    isMultiSelectMode,
    handleMultiSelect,
    setIsCreateFolderDialogOpen,
    processingDocuments,
    user
  ]);

  // Add a SearchBar component
  const SearchBar = ({ value, onChange }) => (
    <div className="relative">
      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Search documents..."
        className="w-full pl-9 pr-4 py-2 text-sm bg-gray-50 dark:bg-gray-700 
          border border-gray-200 dark:border-gray-600 rounded-lg
          focus:outline-none focus:ring-2 focus:ring-blue-500
          placeholder-gray-400 dark:placeholder-gray-500
          text-gray-900 dark:text-white"
      />
    </div>
  );

  // Add CreateFolderDialog component
  const CreateFolderDialog = () => (
    <Dialog open={isCreateFolderDialogOpen} onOpenChange={setIsCreateFolderDialogOpen}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Create New Folder</DialogTitle>
        </DialogHeader>
        <div className="py-4">
          <input
            type="text"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            placeholder="Folder name"
            className="w-full px-3 py-2 
              text-gray-900 dark:text-white 
              placeholder:text-gray-500 dark:placeholder:text-gray-400
              bg-white dark:bg-gray-800 
              border border-gray-300 dark:border-gray-600 
              rounded-md 
              focus:outline-none focus:ring-2 focus:ring-blue-500"
            autoFocus
          />
        </div>
        <DialogFooter>
          <button
            onClick={() => {
              setIsCreateFolderDialogOpen(false);
              setNewFolderName('');
            }}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 
              bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 
              rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              handleCreateFolder();
              setNewFolderName('');
            }}
            disabled={isCreatingFolder || !newFolderName.trim()}
            className="ml-3 px-4 py-2 text-sm font-medium text-white 
              bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700
              rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 
              focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isCreatingFolder ? (
              <Loader className="w-4 h-4 animate-spin" />
            ) : (
              'Create'
            )}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  // Add the rename dialog component
  const RenameSetDialog = () => (
    <Dialog open={!!documentSetToRename} onOpenChange={() => setDocumentSetToRename(null)}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Rename Document Set</DialogTitle>
        </DialogHeader>
        <div className="py-4">
          <input
            type="text"
            value={newSetName}
            onChange={(e) => setNewSetName(e.target.value)}
            placeholder="Enter new name"
            className="w-full px-3 py-2 text-gray-900 dark:text-white 
              placeholder:text-gray-500 dark:placeholder:text-gray-400
              bg-white dark:bg-gray-700 
              border border-gray-300 dark:border-gray-600 
              rounded-md 
              focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <DialogFooter>
          <button
            onClick={() => setDocumentSetToRename(null)}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 
              bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 
              rounded-md hover:bg-gray-50 dark:hover:bg-gray-600"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              if (newSetName.trim()) {
                onRenameDocumentSet(documentSetToRename.id, newSetName.trim());
                setDocumentSetToRename(null);
                setNewSetName('');
              }
            }}
            disabled={isRenamingSet || !newSetName.trim()}
            className="ml-3 px-4 py-2 text-sm font-medium text-white 
              bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700
              rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 
              focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isRenamingSet ? (
              <Loader className="w-4 h-4 animate-spin" />
            ) : (
              'Rename'
            )}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  // Add a helper message when trying to select incompatible documents
  const MultiSelectMessage = () => {
    if (!isMultiSelectMode || selectedDocuments.length === 0) return null;

    const firstDoc = selectedDocuments[0];
    return (
      <div className="px-4 py-2 text-xs text-gray-500 dark:text-gray-400">
        Selecting {getFileTypeName(firstDoc.file_type)} documents only
      </div>
    );
  };

  const handleStartMultiChat = () => {
    if (selectedDocuments.length === 0) return;
    
    onStartMultiChat(selectedDocuments);
    setIsMultiSelectMode(false);
    setSelectedDocuments([]);
  };

  // Add loading skeletons
  const LoadingSkeleton = () => (
    <div className="space-y-2 p-4">
      {[1, 2, 3].map((i) => (
        <div key={i} className="animate-pulse flex items-center space-x-3">
          <div className="w-5 h-5 bg-gray-200 dark:bg-gray-700 rounded" />
          <div className="flex-1 space-y-2">
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4" />
            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
          </div>
        </div>
      ))}
    </div>
  );

  // Add better empty states
  const EmptyState = ({ type }) => (
    <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
      {type === 'documents' ? (
        <>
          <File className="w-12 h-12 text-gray-400 dark:text-gray-600 mb-3" />
          <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-1">
            No documents yet
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
            Upload your first document to get started
          </p>
          <button
            onClick={onNewDocument}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 
              hover:bg-blue-700 rounded-lg transition-colors"
          >
            Upload Document
          </button>
        </>
      ) : type === 'folders' ? (
        <>
          <Folder className="w-12 h-12 text-gray-400 dark:text-gray-600 mb-3" />
          <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-1">
            No folders yet
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
            Create a folder to organize your documents
          </p>
          <button
            onClick={() => setIsCreateFolderDialogOpen(true)}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 
              hover:bg-blue-700 rounded-lg transition-colors"
          >
            Create Folder
          </button>
        </>
      ) : (
        <>
          <Files className="w-12 h-12 text-gray-400 dark:text-gray-600 mb-3" />
          <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-1">
            No items found
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Try adjusting your search
          </p>
        </>
      )}
    </div>
  );

  const handleDocumentClick = (document) => {
    if (document.embedding_status !== 'completed') {
      // Optional: Show a tooltip or message
      return;
    }
    onDocumentSelect(document);
  };

  // Add function to toggle multi-select mode
  const toggleMultiSelectMode = () => {
    setIsMultiSelectMode(prev => !prev);
    setSelectedDocuments([]);
    forceUpdate();
  };

  // Filter documents based on search query
  const filteredDocuments = useMemo(() => {
    return documents.filter(doc => 
      doc.filename.toLowerCase().includes(searchQuery.toLowerCase()) &&
      // Exclude documents that belong to folders from root view
      (!doc.folder_id || expandedFolders[doc.folder_id])
    );
  }, [documents, searchQuery, expandedFolders]);

  // Calculate paginated documents
  const paginatedDocuments = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    return filteredDocuments.filter(doc => !doc.folder_id).slice(start, end);
  }, [filteredDocuments, currentPage, itemsPerPage]);

  // Calculate paginated folders
  const paginatedFolders = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    return folders.slice(start, end);
  }, [folders, currentPage, itemsPerPage]);

  // Add pagination controls component
  const PaginationControls = ({ totalItems }) => {
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    
    return (
      <div className="flex items-center justify-between px-4 py-2 border-t dark:border-gray-700">
        <button
          onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
          disabled={currentPage === 1}
          className="text-sm text-gray-600 dark:text-gray-300 hover:text-blue-600 disabled:opacity-50"
        >
          Previous
        </button>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          Page {currentPage} of {totalPages}
        </span>
        <button
          onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
          disabled={currentPage === totalPages}
          className="text-sm text-gray-600 dark:text-gray-300 hover:text-blue-600 disabled:opacity-50"
        >
          Next
        </button>
      </div>
    );
  };

  // Add virtualization for long lists
  const DocumentList = ({ documents }) => (
    <List
      height={600}
      itemCount={documents.length}
      itemSize={60}
      width="100%"
    >
      {({ index, style }) => (
        <div style={style}>
          {renderDocument(documents[index])}
        </div>
      )}
    </List>
  );

  // Add this component near the other sub-components in DocumentSidebar
  const OnboardingSteps = () => (
    <div className="p-4 space-y-6 animate-fade-in">
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-12 h-12 mb-4 bg-blue-100 dark:bg-blue-900/30 rounded-full">
          <Zap className="w-6 h-6 text-blue-600 dark:text-blue-400" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          Get Started with Predica.ai
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Follow these steps to start analyzing your documents
        </p>
      </div>

      <div className="space-y-4">
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0 mt-1">
            <div className="w-6 h-6 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-full flex items-center justify-center">
              1
            </div>
          </div>
          <div>
            <h4 className="font-medium text-gray-900 dark:text-white">Upload Documents</h4>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Click the '+' button above to upload PDFs, spreadsheets, or text files.
              We support PDF, CSV, Excel, and Word documents.
            </p>
          </div>
        </div>

        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0 mt-1">
            <div className="w-6 h-6 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-full flex items-center justify-center">
              2
            </div>
          </div>
          <div>
            <h4 className="font-medium text-gray-900 dark:text-white">Organize Files</h4>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Create folders by right-clicking in the sidebar. Drag and drop files
              to organize your documents.
            </p>
          </div>
        </div>

        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0 mt-1">
            <div className="w-6 h-6 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-full flex items-center justify-center">
              3
            </div>
          </div>
          <div>
            <h4 className="font-medium text-gray-900 dark:text-white">Start Analyzing</h4>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Select a document to chat with it, or select multiple files to
              compare and analyze together.
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  // Merge API documents with processing documents
  const allDocuments = useMemo(() => {
    const processingIds = new Set(Object.keys(processingDocuments));
    console.log('Processing IDs:', Array.from(processingIds));
    console.log('API Documents:', documents);

    // Create a map of API documents for quick lookup
    const apiDocumentsMap = new Map(documents.map(doc => [doc.id, doc]));
    
    return [
      // Merge API documents with processing state, prioritizing processing state for in-progress documents
      ...Array.from(new Map([
        ...Object.entries(processingDocuments).map(([id, doc]) => [
          id, 
          {
            ...(apiDocumentsMap.get(id) || {}),
            filename: doc.filename,
            // Only override status if document is actually processing
            embedding_status: doc.status === 'processing' ? doc.status : apiDocumentsMap.get(id)?.embedding_status || doc.status,
            processing_progress: doc.progress,
            is_processing: true
          }
        ]),
        // For completed documents, use API state
        ...documents
          .filter(doc => !processingIds.has(doc.id))
          .map(doc => [doc.id, {
            ...doc,
            is_processing: false
          }])
      ]).values())
    ];
  }, [documents, processingDocuments]);

  // Add this effect to log processingDocuments updates
  // useEffect(() => {
  //   console.log('processingDocuments updated:', processingDocuments);
  // }, [processingDocuments]);

  // Return the main component JSX
  return (
    <div className={cn(
      "h-full flex flex-col",
      "bg-white/90 dark:bg-gray-900/40 backdrop-blur-xl",
      "shadow-[0_0_1px_rgba(0,0,0,0.1),0_2px_4px_rgba(0,0,0,0.05)]",
      "border-r border-gray-200/50 dark:border-gray-800/50",
      "lg:w-72 md:w-64 w-full",
      "transition-all duration-300 ease-in-out"
    )}>
      {/* Header */}
      <div className="p-4 border-b border-gray-200/10 dark:border-gray-800/50">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <h2 className={`text-lg font-medium text-gray-900 dark:text-white ${fontClass}`}>
              Predica.ai
            </h2>
            <span className="px-2 py-0.5 text-[10px] font-semibold bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-full uppercase tracking-wider">
              Beta
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={toggleMultiSelectMode}
              className={cn(
                "p-2 rounded-full transition-all duration-300 transform hover:scale-105",
                isMultiSelectMode 
                  ? "bg-blue-500/10 text-blue-600 dark:text-blue-400 shadow-lg"
                  : "text-gray-600 dark:text-gray-400 hover:bg-gray-100/50 dark:hover:bg-gray-800/50"
              )}
              onTouchEnd={(e) => {
                e.preventDefault();
                toggleMultiSelectMode();
              }}
            >
              <CheckSquare className="w-5 h-5" />
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger 
                asChild 
                className="touch-auto"
              >
                <button 
                  className="p-2 text-gray-600 dark:text-gray-400 
                    hover:bg-gray-100/50 dark:hover:bg-gray-800/50 rounded-full
                    transition-all duration-300 transform hover:scale-105"
                  onTouchStart={(e) => e.stopPropagation()}
                >
                  <Plus className="w-5 h-5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" 
                className="bg-white/90 dark:bg-gray-900/90 backdrop-blur-xl 
                  border border-gray-200/20 dark:border-gray-700/20 shadow-xl"
              >
                <DropdownMenuItem onClick={onNewDocument}>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Document
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setIsCreateFolderDialogOpen(true)}>
                  <FolderPlus className="w-4 h-4 mr-2" />
                  New Folder
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        <div className="relative mb-2">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search documents..."
            className="w-full pl-9 pr-4 py-1.5 text-sm 
              bg-gray-50/50 dark:bg-gray-800/50 
              border border-gray-200/20 dark:border-gray-700/20 rounded-lg
              focus:outline-none focus:ring-2 focus:ring-blue-500/50
              text-gray-900 dark:text-white
              placeholder-gray-400 dark:placeholder-gray-500"
          />
        </div>
      </div>

      {/* Add these buttons right below the search input */}
      <div className="p-4 border-b border-gray-200/10 dark:border-gray-800/50">
        {isMultiSelectMode && (
          <div className="space-y-3">
            {/* Chat Button */}
            <button
              onClick={handleStartMultiChat}
              className="w-full px-3 py-2 text-sm font-medium text-white 
                bg-blue-600 hover:bg-blue-700 rounded-md
                flex items-center justify-center space-x-2"
            >
              <MessageSquare className="w-4 h-4" />
              <span>
                Chat with {selectedDocuments.length} {getFileTypeName(selectedDocuments[0]?.file_type || '')}s
              </span>
            </button>

            {/* Action Buttons Row */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  // If there are already selected documents, use the first one as reference
                  if (selectedDocuments.length > 0) {
                    const referenceDoc = selectedDocuments[0];
                    const compatibleDocs = paginatedDocuments.filter(doc => 
                      doc?.id && isCompatibleDocumentType(doc, referenceDoc)
                    );
                    setSelectedDocuments(prev => [
                      ...prev,
                      ...compatibleDocs.filter(doc => 
                        !prev.some(p => p.id === doc.id)
                      )
                    ]);
                  } else {
                    // If no documents are selected yet, select all PDFs by default
                    const pdfDocs = paginatedDocuments.filter(doc => 
                      doc?.id && doc.file_type.includes('pdf')
                    );
                    setSelectedDocuments(pdfDocs);
                  }
                }}
                className="flex-1 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-200 
                  bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md"
              >
                Select All
              </button>

              <button
                onClick={handleDeleteSelected}
                disabled={selectedDocuments.length === 0}
                className="flex-1 px-3 py-1.5 text-sm font-medium text-red-600 dark:text-red-400 
                  bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 
                  rounded-md disabled:opacity-50"
              >
                Delete Selected
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Folders section - Moved above document sets */}
      {folders.length > 0 && (
        <div className="px-4 pt-4">
          <div className="px-3 py-2 flex items-center justify-between">
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Folders ({folders.length})
            </span>
            <button 
              onClick={() => setIsCreateFolderDialogOpen(true)}
              className="text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 p-1 rounded-md"
            >
              <FolderPlus className="w-5 h-5" />
            </button>
          </div>
          {paginatedFolders.map(folder => (
            <FolderItem
              key={folder.id}
              folder={folder}
              documents={documents} // Pass all documents instead of filtered
            />
          ))}
          {folders.length > itemsPerPage && (
            <PaginationControls totalItems={folders.length} />
          )}
        </div>
      )}

      {/* Document Sets */}
      {documentSets.length > 0 && (
        <div className="px-4 pt-2 pb-3">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
            Document Sets
          </h3>
          <div className="space-y-2">
            {documentSets.map(set => (
              <DocumentSetItem key={set.id} set={set} />
            ))}
          </div>
        </div>
      )}

      {/* Root Documents - Moved below folders */}
      <div className="px-4 pt-3 space-y-2">
        {console.log('Rendering documents:', documents)}
        {documents.length > 0 ? (
          <>
            <DocumentList documents={paginatedDocuments} />
            {filteredDocuments.filter(doc => !doc.folder_id).length > itemsPerPage && (
              <PaginationControls 
                totalItems={filteredDocuments.filter(doc => !doc.folder_id).length} 
              />
            )}
          </>
        ) : (
          <div className="p-4 text-gray-500">No documents found</div>
        )}
      </div>

      {/* Create Folder Dialog */}
      <CreateFolderDialog />

      {/* Add the rename dialog */}
      <RenameSetDialog />

      {/* Add the helper message */}
      <MultiSelectMessage />
    </div>
  );
};

DocumentSidebar.defaultProps = {
  fetchDocuments: async () => {
    try {
      const response = await axios.get('/api/v1/documents');
      console.log('API Documents Response:', response.data);
      // Update your documents state with response.data
    } catch (error) {
      console.error('Error fetching documents:', error);
    }
  },
  // ... other default props
};

export default DocumentSidebar;